import { useCallback, useEffect, useRef, useState } from "react";
import { getWorkspaceDetailApi } from "../../api/workspaceApi";

/** Read-only pages of paid answers retained outside the current live query. */
export function useSavedSearchHistory(id, workspace, detailRouteRef) {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const loadingRef = useRef(false);
  const requestRef = useRef(0);

  const ingest = useCallback((detail) => {
    requestRef.current += 1;
    loadingRef.current = false;
    setLoading(false);
    setRows(detail?.previously_enriched_rows || []);
    setColumns(detail?.columns || []);
    setPage(1);
    setError("");
  }, []);

  useEffect(() => {
    requestRef.current += 1;
    loadingRef.current = false;
    setRows([]);
    setColumns([]);
    setPage(1);
    setLoading(false);
    setError("");
    return () => { requestRef.current += 1; };
  }, [id]);

  const loadMore = useCallback(async () => {
    const total = workspace?.previously_enriched_company_count || 0;
    if (loadingRef.current || rows.length >= total) return;
    loadingRef.current = true;
    const requestId = ++requestRef.current;
    const isCurrent = () => detailRouteRef.current === id && requestRef.current === requestId;
    setLoading(true);
    setError("");
    const nextPage = page + 1;
    try {
      const response = await getWorkspaceDetailApi(id, nextPage, 100);
      if (!isCurrent()) return;
      const nextRows = response?.data?.previously_enriched_rows;
      if (response?.status !== 200 || !Array.isArray(nextRows) || nextRows.length === 0) {
        setError("Could not load more previously enriched results. Try again.");
        return;
      }
      setRows((current) => {
        const existing = new Set(current.map((row) => row.stagedId).filter(Boolean));
        return [...current, ...nextRows.filter((row) => !row.stagedId || !existing.has(row.stagedId))];
      });
      setColumns((current) => [
        ...current,
        ...(response.data.columns || []).filter((column) => !current.includes(column)),
      ]);
      setPage(nextPage);
    } catch (_error) {
      if (isCurrent()) setError("Could not load more previously enriched results. Try again.");
    } finally {
      if (isCurrent()) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [detailRouteRef, id, page, rows.length, workspace?.previously_enriched_company_count]);

  return { rows, columns, loading, error, ingest, loadMore };
}
